// pages/TravelExpense/TravelExpenseForm.js
//
// Create-new-request / edit-draft form for Travel Expense Approval.
// Mirrors the legacy frmTravelExpense.vb layout:
//
//   Part 1 — Overview
//     Name of Trip, Primary Objective, Traveler's Name (read-only),
//     Leave Home Date, Return to Home Date,
//     Accompanying Employees (add/remove list)
//
//   Part 2 — Destinations
//     One row per company visited: Start Date, End Date, # Hours,
//     Nature of Visit, Company, City State Country, Contact, Goal.
//     Rows are added/removed inline.
//
//   Part 3 — Estimated Expenses
//     Airfare, Lodging, Meals, Entertainment, Car Rental, Personal Car,
//     Taxi, Misc + Misc description. Estimated Total auto-computes.
//
// Two save actions: "Save & Close" (Status=1 draft) and "Submit For
// Approval" (Status=2). The page works in two modes — /travel-expense/new
// renders blank; /travel-expense/:id/edit hydrates from GET edit-data.
//
// Routes:
//   /travel-expense/new        → blank create
//   /travel-expense/:id/edit   → edit existing draft (status 1 or 2)

import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, Row, Col, Input, Label, Button,
    Spinner, Badge, Alert, FormGroup,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";

// ─── helpers ──────────────────────────────────────────────────────────────
const toIsoDate = (d) => {
    if (!d) return "";
    try {
        const dt = new Date(d);
        const y = dt.getFullYear(); const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    } catch { return ""; }
};
const todayIso = () => toIsoDate(new Date());

const fmtMoney = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
// Numeric-only input handler — strips non-numeric / multi-decimal entries.
const cleanMoney = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    return parts.length > 1 ? parts[0] + "." + parts.slice(1).join("") : s;
};

// ─── blank-form initial state ─────────────────────────────────────────────
const blankDestination = () => ({
    startDate: todayIso(),
    endDate: todayIso(),
    numberOfHours: 0,
    natureOfVisit: "",
    company: "",
    cityStateCountry: "",
    contactPerson: "",
    goal: "",
});
const blankForm = () => ({
    nameOfTrip: "",
    primaryObjective: "",
    leaveHomeDate: todayIso(),
    returnHomeDate: todayIso(),
    accompanyingEmployeeIds: [],     // ints
    destinations: [],                // array of blankDestination()
    airfare: "0", lodging: "0", meals: "0", entertainment: "0",
    carRental: "0", personalCar: "0", taxi: "0", other: "0",
    otherNote: "",
});

const TravelExpenseForm = () => {
    const { id } = useParams();           // undefined for /new
    const navigate = useNavigate();
    const auth = useAuth();

    const isEdit = Boolean(id);
    const [form, setForm] = useState(blankForm);
    const [loading, setLoading] = useState(isEdit);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // Inline "add accompanying employee" input — accepts a raw empId.
    // (Future enhancement: search by name via /api/users.)
    const [employeeInput, setEmployeeInput] = useState("");

    // ─── load existing draft for edit mode ───────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        let alive = true;
        setLoading(true); setError(null);
        api.get(`TravelExpense/${id}/edit-data`)
            .then(({ data }) => {
                if (!alive) return;
                const t = data?.Trip ?? {};
                const dests = (data?.Destinations ?? []).map((d) => ({
                    startDate: toIsoDate(d.startDate),
                    endDate: toIsoDate(d.endDate),
                    numberOfHours: d.numberOfHours ?? 0,
                    natureOfVisit: d.natureOfVisit ?? "",
                    company: d.company ?? "",
                    cityStateCountry: d.cityStateCountry ?? "",
                    contactPerson: d.contactPerson ?? "",
                    goal: d.goal ?? "",
                }));
                // Exclude the requester from the accompanying list (they're
                // always the trip owner — we manage that server-side).
                const meInt = parseInt(auth.empId ?? auth.uid ?? "0", 10);
                const accompanying = (data?.EmployeeIds ?? []).filter((e) => e !== meInt);
                setForm({
                    nameOfTrip: t.nameOfTrip ?? "",
                    primaryObjective: t.primaryObjective ?? "",
                    leaveHomeDate: toIsoDate(t.leaveHomeDate) || todayIso(),
                    returnHomeDate: toIsoDate(t.returnHomeDate) || todayIso(),
                    accompanyingEmployeeIds: accompanying,
                    destinations: dests,
                    airfare: String(t.airfare ?? 0),
                    lodging: String(t.lodging ?? 0),
                    meals: String(t.meals ?? 0),
                    entertainment: String(t.entertainment ?? 0),
                    carRental: String(t.carRental ?? 0),
                    personalCar: String(t.personalCar ?? 0),
                    taxi: String(t.taxi ?? 0),
                    other: String(t.other ?? 0),
                    otherNote: t.otherNote ?? "",
                });
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load draft"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [id, isEdit, auth.empId, auth.uid]);

    // ─── computed total ──────────────────────────────────────────────────
    const total = useMemo(() => {
        const sum = ["airfare", "lodging", "meals", "entertainment", "carRental", "personalCar", "taxi", "other"]
            .reduce((acc, k) => acc + Number(form[k] || 0), 0);
        return sum;
    }, [form]);

    // ─── small helpers ────────────────────────────────────────────────────
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setMoney = (k, v) => setField(k, cleanMoney(v));

    const addDestination = () => setForm((f) => ({ ...f, destinations: [...f.destinations, blankDestination()] }));
    const removeDestination = (i) => setForm((f) => ({ ...f, destinations: f.destinations.filter((_, idx) => idx !== i) }));
    const setDest = (i, k, v) => setForm((f) => ({
        ...f,
        destinations: f.destinations.map((d, idx) => idx === i ? { ...d, [k]: v } : d),
    }));

    const addEmployee = () => {
        const n = parseInt((employeeInput || "").trim(), 10);
        if (!Number.isFinite(n) || n <= 0) return;
        if (form.accompanyingEmployeeIds.includes(n)) { setEmployeeInput(""); return; }
        setForm((f) => ({ ...f, accompanyingEmployeeIds: [...f.accompanyingEmployeeIds, n] }));
        setEmployeeInput("");
    };
    const removeEmployee = (e) =>
        setForm((f) => ({ ...f, accompanyingEmployeeIds: f.accompanyingEmployeeIds.filter((x) => x !== e) }));

    // ─── submit handler ───────────────────────────────────────────────────
    const save = async (submitForApproval) => {
        setBusy(true); setError(null);
        const payload = {
            nameOfTrip: form.nameOfTrip,
            primaryObjective: form.primaryObjective,
            leaveHomeDate: form.leaveHomeDate || null,
            returnHomeDate: form.returnHomeDate || null,
            accompanyingEmployeeIds: form.accompanyingEmployeeIds,
            destinations: form.destinations.map((d) => ({
                startDate: d.startDate,
                endDate: d.endDate,
                numberOfHours: Number(d.numberOfHours) || 0,
                natureOfVisit: d.natureOfVisit,
                company: d.company,
                cityStateCountry: d.cityStateCountry,
                contactPerson: d.contactPerson,
                goal: d.goal,
            })),
            airfare: Number(form.airfare) || 0,
            lodging: Number(form.lodging) || 0,
            meals: Number(form.meals) || 0,
            entertainment: Number(form.entertainment) || 0,
            carRental: Number(form.carRental) || 0,
            personalCar: Number(form.personalCar) || 0,
            taxi: Number(form.taxi) || 0,
            other: Number(form.other) || 0,
            otherNote: form.otherNote,
            submitForApproval,
        };
        try {
            if (isEdit) await api.put(`TravelExpense/${id}`, payload);
            else        await api.post("TravelExpense", payload);
            navigate("/travel-expense");
        } catch (e) {
            // Backend error envelope: { error, type, inner, where }.
            // Inner is the actual SQL Server message (NOT NULL violation,
            // FK constraint, etc) — show it because the outer EF wrapper
            // alone never tells you what's wrong.
            const d = e?.response?.data;
            const outer = d?.error  || e?.message || "Save failed";
            const inner = d?.inner;
            setError(inner ? `${outer}  →  ${inner}` : outer);
            setBusy(false);
        }
    };

    if (loading) return (<div className="page-content"><Container fluid><div className="text-center py-5"><Spinner color="primary" /></div></Container></div>);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={isEdit ? `Edit Trip #${id}` : "New Travel Expense Request"}
                    breadcrumb={[{ label: "Travel Expense", to: "/workflows?wf=travel" }, { label: isEdit ? `Edit Trip #${id}` : "New Travel Expense Request" }]}
                    subtitle="Submit a request for travel expense approval"
                    breadcrumbItems={[
                        { label: "Travel Expense", href: "/travel-expense" },
                        { label: isEdit ? "Edit" : "New" },
                    ]}
                />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}

                {/* ─── PART 1 — OVERVIEW ─────────────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <h5 className="text-uppercase text-muted mb-3" style={{ letterSpacing: 1 }}>Part 1 — Overview</h5>
                        <Row className="g-3">
                            <Col md={8}>
                                <Label>Name of Trip</Label>
                                <Input value={form.nameOfTrip} onChange={(e) => setField("nameOfTrip", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={4}>
                                <Label>Traveler</Label>
                                <Input value={auth.name || auth.empId || ""} disabled />
                            </Col>
                            <Col md={12}>
                                <Label>Primary Objective</Label>
                                <Input type="textarea" rows={2} value={form.primaryObjective}
                                    onChange={(e) => setField("primaryObjective", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={3}>
                                <Label>Leave Home Date</Label>
                                <Input type="date" value={form.leaveHomeDate}
                                    onChange={(e) => setField("leaveHomeDate", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={3}>
                                <Label>Return to Home Date</Label>
                                <Input type="date" value={form.returnHomeDate}
                                    onChange={(e) => setField("returnHomeDate", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={6}>
                                <Label>Accompanying Employees (empId)</Label>
                                <div className="d-flex gap-2">
                                    <Input value={employeeInput}
                                        placeholder="e.g. 3297"
                                        onChange={(e) => setEmployeeInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmployee(); } }}
                                        disabled={busy} />
                                    <Button color="primary" outline size="sm" onClick={addEmployee} disabled={busy}>Add</Button>
                                </div>
                                {form.accompanyingEmployeeIds.length > 0 && (
                                    <div className="mt-2">
                                        {form.accompanyingEmployeeIds.map((e) => (
                                            <Badge key={e} color="info" pill className="me-2 mb-1" style={{ cursor: "pointer" }}
                                                onClick={() => !busy && removeEmployee(e)} title="Click to remove">
                                                {e} ×
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* ─── PART 2 — DESTINATIONS ──────────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-uppercase text-muted mb-0" style={{ letterSpacing: 1 }}>Part 2 — Destinations</h5>
                            <Button color="primary" outline size="sm" onClick={addDestination} disabled={busy}>+ Add Destination</Button>
                        </div>

                        {form.destinations.length === 0 ? (
                            <div className="text-muted text-center py-3">
                                No destinations yet. Click "Add Destination" to add one row per company you'll visit.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 130 }}>Start Date</th>
                                            <th style={{ width: 130 }}>End Date</th>
                                            <th style={{ width: 80 }}># Hours</th>
                                            <th>Nature of Visit</th>
                                            <th>Company</th>
                                            <th>City, State, Country</th>
                                            <th>Contact</th>
                                            <th>Goal</th>
                                            <th style={{ width: 40 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.destinations.map((d, i) => (
                                            <tr key={i}>
                                                <td><Input type="date" bsSize="sm" value={d.startDate}
                                                    onChange={(e) => setDest(i, "startDate", e.target.value)} disabled={busy} /></td>
                                                <td><Input type="date" bsSize="sm" value={d.endDate}
                                                    onChange={(e) => setDest(i, "endDate", e.target.value)} disabled={busy} /></td>
                                                <td><Input type="number" bsSize="sm" min="0" value={d.numberOfHours}
                                                    onChange={(e) => setDest(i, "numberOfHours", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.natureOfVisit}
                                                    onChange={(e) => setDest(i, "natureOfVisit", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.company}
                                                    onChange={(e) => setDest(i, "company", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.cityStateCountry}
                                                    onChange={(e) => setDest(i, "cityStateCountry", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.contactPerson}
                                                    onChange={(e) => setDest(i, "contactPerson", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.goal}
                                                    onChange={(e) => setDest(i, "goal", e.target.value)} disabled={busy} /></td>
                                                <td className="text-center">
                                                    <Button color="link" size="sm" className="text-danger p-0"
                                                        onClick={() => removeDestination(i)} disabled={busy}>×</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* ─── PART 3 — ESTIMATED EXPENSES ─────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <h5 className="text-uppercase text-muted mb-3" style={{ letterSpacing: 1 }}>Part 3 — Estimated Expenses (for the entire group)</h5>
                        <Row className="g-3">
                            {[
                                ["airfare", "Airfare"],
                                ["lodging", "Lodging"],
                                ["meals", "Meals"],
                                ["entertainment", "Entertainment"],
                                ["carRental", "Car Rental"],
                                ["personalCar", "Personal Car"],
                                ["taxi", "Taxi"],
                                ["other", "Misc Expenses"],
                            ].map(([k, label]) => (
                                <Col md={3} key={k}>
                                    <Label>{label}</Label>
                                    <div className="d-flex align-items-center">
                                        <span className="me-1">$</span>
                                        <Input bsSize="sm" inputMode="decimal" value={form[k]}
                                            onChange={(e) => setMoney(k, e.target.value)} disabled={busy} />
                                    </div>
                                </Col>
                            ))}
                            <Col md={12}>
                                <Label>Misc Expenses Description</Label>
                                <Input value={form.otherNote}
                                    onChange={(e) => setField("otherNote", e.target.value)} disabled={busy} />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3 pt-3 border-top">
                            <h5 className="mb-0">Estimated Total:&nbsp;<strong>{fmtMoney(total)}</strong></h5>
                        </div>
                    </CardBody>
                </Card>

                {/* ─── action bar ───────────────────────────────────────── */}
                <div className="mt-3 d-flex justify-content-between">
                    <Button color="link" onClick={() => navigate("/travel-expense")} disabled={busy}>Cancel</Button>
                    <div className="d-flex gap-2">
                        <Button color="secondary" outline onClick={() => save(false)} disabled={busy}>
                            {busy ? <Spinner size="sm" /> : "Save & Close (Draft)"}
                        </Button>
                        <Button color="primary" onClick={() => save(true)} disabled={busy}>
                            {busy ? <Spinner size="sm" /> : "Submit For Approval"}
                        </Button>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default TravelExpenseForm;
